import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface ConfirmCloseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasUnsavedChanges: boolean;
  onConfirmDiscard: () => void;
  onConfirmSave?: () => void | Promise<void>;
  isSaving?: boolean;
  children: React.ReactNode;
}

export function ConfirmCloseDialog({
  open,
  onOpenChange,
  hasUnsavedChanges,
  onConfirmDiscard,
  onConfirmSave,
  isSaving = false,
  children
}: ConfirmCloseDialogProps) {
  const [showWarning, setShowWarning] = useState(false);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen && hasUnsavedChanges) {
      // User trying to close with unsaved changes - show inline warning
      setShowWarning(true);
    } else {
      setShowWarning(false);
      onOpenChange(newOpen);
    }
  }, [hasUnsavedChanges, onOpenChange]);

  // Reset warning state when dialog closes
  useEffect(() => {
    if (!open) {
      setShowWarning(false);
    }
  }, [open]);

  const handleDiscard = useCallback(() => {
    setShowWarning(false);
    onConfirmDiscard();
    onOpenChange(false);
  }, [onConfirmDiscard, onOpenChange]);

  const handleSave = useCallback(async () => {
    if (onConfirmSave) {
      await onConfirmSave();
      setShowWarning(false);
    }
  }, [onConfirmSave]);

  const handleBackToEdit = useCallback(() => {
    setShowWarning(false);
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {showWarning ? (
        <DialogContent className="max-w-md p-6">
          <div className="space-y-6">
            <div className="flex items-start gap-4 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                  Niet-opgeslagen wijzigingen
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Je hebt wijzigingen aangebracht die nog niet zijn opgeslagen.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button 
                variant="ghost" 
                onClick={handleBackToEdit}
                className="w-full sm:w-auto"
              >
                Terug
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDiscard}
                className="w-full sm:w-auto text-destructive hover:text-destructive"
              >
                Verwerpen
              </Button>
              {onConfirmSave && (
                <Button 
                  onClick={handleSave} 
                  disabled={isSaving}
                  className="w-full sm:w-auto"
                >
                  {isSaving ? 'Opslaan...' : 'Opslaan'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      ) : (
        children
      )}
    </Dialog>
  );
}

// Re-export Dialog parts for convenience
export { DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter };
